import React, { useContext, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Button, Card, Empty, Spin, Table, Tag, theme, Tooltip } from 'antd';
import type { ApexOptions } from 'apexcharts';

import {
  isDarkBackground,
  sequentialColor,
  severityColors,
  trendColors,
} from '@totem/components/software/vulnerabilityReport/summary/chartColors';
import AgingDashboardContext from '@totem/components/ticketing/agingDashboard/AgingDashboardContext';
import TicketCell from '@totem/components/ticketing/agingDashboard/TicketCell';
import {
  AgingGroup,
  AgingResponsePriority,
  AgingTicket,
  TicketsDrill,
} from '@totem/components/ticketing/agingDashboard/types';
import {
  formatDate,
  formatDays,
  formatHours,
  formatThreshold,
  isActive,
  PRIORITIES,
  PRIORITY_LABELS,
  PRIORITY_SEVERITY,
  PRIORITY_TAG_COLORS,
} from '@totem/components/ticketing/agingDashboard/utilities';
import KpiCard from '@totem/components/ui_components/widgets/KpiCard';

type Props = {
  onDrillDown: (drill: TicketsDrill) => void;
};

const cardStyles = { body: { padding: 12 } };

const twoColumn: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
  gap: 16,
};

const UPDATED_BY_LABELS = {
  ib: 'Intelligent Buildings',
  customer: 'Customer',
  none: 'No reply yet',
};

const AgingSummaryTab = ({ onDrillDown }: Props) => {
  const { loading, data, measure } = useContext(AgingDashboardContext);
  const { token } = theme.useToken();
  const isDark = isDarkBackground(token.colorBgContainer);
  const severity = severityColors(isDark);

  const chartBase = useMemo(
    (): ApexOptions => ({
      chart: {
        toolbar: { show: false },
        animations: { enabled: false },
        background: 'transparent',
        fontFamily: 'inherit',
      },
      theme: { mode: isDark ? 'dark' : 'light' },
      grid: { borderColor: token.colorBorderSecondary },
      dataLabels: { enabled: false },
      legend: {
        position: 'bottom',
        fontSize: '11px',
        labels: { colors: token.colorTextSecondary },
      },
      tooltip: { theme: isDark ? 'dark' : 'light' },
    }),
    [isDark, token.colorBorderSecondary, token.colorTextSecondary],
  );

  if (loading && !data) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!data) {
    return <Empty description="No data" />;
  }

  const { kpis } = data;
  const secondary = { color: token.colorTextSecondary };
  const sectionLabel: React.CSSProperties = {
    fontSize: 10.5,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: token.colorTextSecondary,
    fontWeight: 600,
  };
  const priorityTag = (priority: string) => (
    <Tag
      color={PRIORITY_TAG_COLORS[priority] ?? 'default'}
      style={{ marginInlineEnd: 0 }}
    >
      {priority || '-'}
    </Tag>
  );
  const integerLabel = (value: number) => `${Math.round(value)}`;

  // Distribution, stacked by priority.
  const buckets = measure === 'update' ? data.sinceUpdate : data.sinceOpened;
  const bucketPriorities = PRIORITIES.filter((priority) =>
    buckets.some((bucket) => (bucket.byPriority[priority] ?? 0) > 0),
  );
  const distributionOptions: ApexOptions = {
    ...chartBase,
    chart: {
      ...chartBase.chart,
      type: 'bar',
      stacked: true,
      events: {
        dataPointSelection: (_event, _chart, config) => {
          const bucket = buckets[config.dataPointIndex];
          if (bucket) {
            onDrillDown({ view: 'all', bucketKey: bucket.key });
          }
        },
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '55%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    stroke: { width: 2, colors: [token.colorBgContainer] },
    colors: bucketPriorities.map(
      (priority) => severity[PRIORITY_SEVERITY[priority]],
    ),
    xaxis: {
      categories: buckets.map((bucket) => bucket.label),
      labels: { style: { colors: token.colorTextSecondary } },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        style: { colors: token.colorTextSecondary },
        formatter: integerLabel,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: isDark ? 'dark' : 'light',
    },
  };

  // Trend: open and quiet counts on the left axis, median age (days) on the right.
  const trend = trendColors(isDark);
  const trendOptions: ApexOptions = {
    ...chartBase,
    chart: { ...chartBase.chart, type: 'line', zoom: { enabled: false } },
    colors: [trend[0], trend[1], trend[2]],
    stroke: { width: [2, 2, 2], curve: 'straight', dashArray: [0, 0, 5] },
    xaxis: {
      type: 'datetime',
      labels: { style: { colors: token.colorTextSecondary } },
    },
    yaxis: [
      {
        seriesName: 'Open',
        min: 0,
        forceNiceScale: true,
        labels: {
          style: { colors: token.colorTextSecondary },
          formatter: integerLabel,
        },
      },
      { seriesName: 'Open', show: false },
      {
        seriesName: 'Median age (days)',
        opposite: true,
        min: 0,
        forceNiceScale: true,
        labels: {
          style: { colors: token.colorTextSecondary },
          formatter: integerLabel,
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      x: { format: 'MMM d, yyyy' },
      theme: isDark ? 'dark' : 'light',
    },
  };
  const trendSeries = [
    {
      name: 'Open',
      data: data.trend.map((point) => ({ x: point.date, y: point.open })),
    },
    {
      name: 'No update 7+ days',
      data: data.trend.map((point) => ({ x: point.date, y: point.stale7 })),
    },
    {
      name: 'Median age (days)',
      data: data.trend.map((point) => ({
        x: point.date,
        y: point.medianAgeDays,
      })),
    },
  ];

  const active = data.tickets.filter(isActive);
  const needsUpdate = active
    .filter((ticket) => ticket.breached || ticket.staleDays >= 7)
    .slice(0, 6);
  const maxBuildingOpen = Math.max(
    1,
    ...data.byBuilding.map((group) => group.open),
  );
  const lastUpdateTotal =
    data.lastUpdateBy.intelligentBuildings +
    data.lastUpdateBy.customer +
    data.lastUpdateBy.noReply;
  const lastUpdateParts = [
    {
      key: 'ib',
      value: data.lastUpdateBy.intelligentBuildings,
      color: token.colorPrimary,
    },
    { key: 'customer', value: data.lastUpdateBy.customer, color: trend[1] },
    {
      key: 'none',
      value: data.lastUpdateBy.noReply,
      color: token.colorTextQuaternary,
    },
  ];

  const sinceUpdateCell = (ticket: AgingTicket) => {
    const background =
      ticket.breached || ticket.staleDays >= 14
        ? token.colorErrorBg
        : ticket.staleDays >= 7
          ? token.colorWarningBg
          : token.colorSuccessBg;
    const color =
      ticket.breached || ticket.staleDays >= 14
        ? token.colorErrorText
        : ticket.staleDays >= 7
          ? token.colorWarningText
          : token.colorSuccessText;
    return (
      <span
        style={{
          display: 'inline-block',
          minWidth: 44,
          textAlign: 'center',
          padding: '1px 6px',
          borderRadius: 4,
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          background,
          color,
        }}
      >
        {ticket.pendingCustomer ? 'Customer' : formatDays(ticket.staleDays)}
      </span>
    );
  };

  const groupColumns = (label: string) => [
    { title: label, dataIndex: 'label', key: 'label', ellipsis: true },
    { title: 'Open', dataIndex: 'open', key: 'open', align: 'right' as const },
    {
      title: 'Median age',
      dataIndex: 'medianAgeDays',
      key: 'medianAgeDays',
      align: 'right' as const,
      render: (value: number) => formatDays(value),
    },
    {
      title: 'Since update',
      dataIndex: 'medianStaleDays',
      key: 'medianStaleDays',
      align: 'right' as const,
      render: (value: number) => formatDays(value),
    },
    {
      title: 'Breached',
      dataIndex: 'breached',
      key: 'breached',
      align: 'right' as const,
      render: (value: number) =>
        value > 0 ? (
          <span style={{ color: token.colorErrorText, fontWeight: 600 }}>
            {value}
          </span>
        ) : (
          value
        ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10,
        }}
      >
        <KpiCard
          label="Open tickets"
          value={kpis.open}
          sub={`${kpis.resolved} resolved, awaiting close · ${kpis.pendingCustomer} pending customer`}
          onClick={() => onDrillDown({ view: 'all' })}
        />
        <KpiCard
          label="Median age"
          value={formatDays(kpis.medianAgeDays)}
          sub={`oldest ${formatDays(kpis.oldestDays)} · ${kpis.over30Days} over 30 days`}
        />
        <KpiCard
          label="No update · 7+ days"
          value={kpis.stale7}
          sub={`${kpis.stale14} over 14 days · ${kpis.stale30} over 30 days`}
          valueColor={kpis.stale7 > 0 ? token.colorWarningText : undefined}
          onClick={() => onDrillDown({ view: 'stale7' })}
        />
        <KpiCard
          label="SLA breached"
          value={kpis.breached}
          sub={`${kpis.atRisk} at risk in the next 24h`}
          valueColor={kpis.breached > 0 ? token.colorErrorText : undefined}
          onClick={() => onDrillDown({ view: 'breached' })}
        />
        <KpiCard
          label="Awaiting first reply"
          value={kpis.awaitingFirstReply}
          sub={
            kpis.awaitingFirstReply > 0
              ? `no reply from a person yet · oldest ${formatDays(kpis.oldestAwaitingReplyDays)}`
              : 'Every open ticket has a reply'
          }
          onClick={() => onDrillDown({ view: 'awaitingReply' })}
        />
        <KpiCard
          label={`Median first reply · ${data.historyDays}d`}
          value={formatHours(kpis.medianFirstReplyHours)}
          sub={
            kpis.medianFirstReplyHours >= 0
              ? `${kpis.firstReplyMetPct}% within the SLA target`
              : 'No replies in this period'
          }
        />
      </div>

      <div style={twoColumn}>
        <Card
          size="small"
          title={
            measure === 'update'
              ? 'Days since last update'
              : 'Days since opened'
          }
          extra={
            <span style={{ fontSize: 12 }}>
              Open tickets by priority · click a bar for the list
            </span>
          }
          styles={cardStyles}
        >
          {kpis.open > 0 ? (
            <ReactApexChart
              type="bar"
              height={260}
              options={distributionOptions}
              series={bucketPriorities.map((priority) => ({
                name: PRIORITY_LABELS[priority],
                data: buckets.map((bucket) => bucket.byPriority[priority] ?? 0),
              }))}
            />
          ) : (
            <Empty description="No open tickets" />
          )}
        </Card>

        <Card
          size="small"
          title="SLA by priority"
          extra={
            <Button
              type="link"
              size="small"
              onClick={() => onDrillDown({ view: 'breached' })}
            >
              View breached
            </Button>
          }
          styles={cardStyles}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.slaByPriority.map((row) => (
              <div key={row.priority}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    fontSize: 13,
                    marginBottom: 4,
                  }}
                >
                  <span>
                    <b>{row.priority}</b>{' '}
                    <span style={secondary}>
                      · within {formatThreshold(row.thresholdHours)}
                    </span>
                  </span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {row.open} open
                    {row.atRisk > 0 && (
                      <span style={{ color: token.colorWarningText }}>
                        {' '}
                        · {row.atRisk} at risk
                      </span>
                    )}
                    {row.breached > 0 && (
                      <span
                        style={{ color: token.colorErrorText, fontWeight: 600 }}
                      >
                        {' '}
                        · {row.breached} breached
                      </span>
                    )}
                  </span>
                </div>
                <div
                  role="img"
                  aria-label={`${row.priority}: ${row.within} within, ${row.atRisk} at risk, ${row.breached} breached`}
                  style={{
                    display: 'flex',
                    height: 14,
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: token.colorFillSecondary,
                  }}
                >
                  {row.open > 0 &&
                    [
                      { value: row.within, color: token.colorSuccess },
                      { value: row.atRisk, color: token.colorWarning },
                      { value: row.breached, color: token.colorError },
                    ]
                      .filter((part) => part.value > 0)
                      .map((part) => (
                        <span
                          key={part.color}
                          style={{
                            width: `${(part.value / row.open) * 100}%`,
                            background: part.color,
                          }}
                        />
                      ))}
                </div>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px 14px',
                fontSize: 12,
              }}
            >
              {[
                { label: 'Within', color: token.colorSuccess },
                { label: 'At risk (24h)', color: token.colorWarning },
                { label: 'Breached', color: token.colorError },
              ].map((part) => (
                <span key={part.label}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: part.color,
                      marginRight: 6,
                    }}
                  />
                  {part.label}
                </span>
              ))}
            </div>
            <div style={{ ...secondary, fontSize: 12 }}>
              Same SLA targets as the Ticketing Dashboard, measured from when
              the ticket was opened. Projects, Optimize and resolved tickets are
              excluded.
            </div>
          </div>
        </Card>
      </div>

      <div style={twoColumn}>
        <Card
          size="small"
          title="Needs an update"
          extra={
            <span style={{ fontSize: 12 }}>
              SLA breached, then longest without an update
            </span>
          }
          styles={cardStyles}
        >
          <Table
            size="small"
            pagination={false}
            scroll={{ x: true }}
            dataSource={needsUpdate}
            rowKey={(ticket) => ticket.id}
            locale={{
              emptyText:
                'Every open ticket is within its SLA and updated in the last week',
            }}
            columns={[
              {
                title: 'Ticket',
                key: 'ticket',
                render: (_, ticket: AgingTicket) => (
                  <TicketCell ticket={ticket} />
                ),
              },
              {
                title: 'Building',
                dataIndex: 'buildingName',
                key: 'buildingName',
                ellipsis: true,
              },
              {
                title: 'Priority',
                key: 'priority',
                render: (_, ticket: AgingTicket) =>
                  priorityTag(ticket.priority),
              },
              {
                title: 'Age',
                key: 'age',
                align: 'right' as const,
                render: (_, ticket: AgingTicket) => formatDays(ticket.ageDays),
              },
              {
                title: 'Since update',
                key: 'stale',
                align: 'right' as const,
                render: (_, ticket: AgingTicket) => sinceUpdateCell(ticket),
              },
              {
                title: 'Last update by',
                key: 'by',
                render: (_, ticket: AgingTicket) =>
                  ticket.lastUpdateBy === 'none' ? (
                    <span style={secondary}>No reply yet</span>
                  ) : (
                    UPDATED_BY_LABELS[ticket.lastUpdateBy]
                  ),
              },
              {
                title: 'Follow-up',
                key: 'followUp',
                render: (_, ticket: AgingTicket) =>
                  ticket.followUpAt ? (
                    <span
                      style={{
                        color: ticket.followUpOverdue
                          ? token.colorErrorText
                          : undefined,
                      }}
                    >
                      {formatDate(ticket.followUpAt)}
                      {ticket.followUpOverdue ? ' · overdue' : ''}
                    </span>
                  ) : (
                    <span style={secondary}>—</span>
                  ),
              },
            ]}
          />
          <div style={{ marginTop: 8, fontSize: 12 }}>
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => onDrillDown({ view: 'stale7' })}
            >
              See all {kpis.stale7} tickets with no update for 7+ days →
            </Button>
          </div>
        </Card>

        <Card size="small" title="Aging by type" styles={cardStyles}>
          <Table
            size="small"
            pagination={false}
            dataSource={data.byType}
            rowKey={(group) => group.key || group.label}
            columns={groupColumns('Type')}
            onRow={(group: AgingGroup) => ({
              onClick: () => onDrillDown({ view: 'all', type: group.key }),
              style: { cursor: 'pointer' },
            })}
          />
          <div style={{ ...sectionLabel, margin: '16px 0 8px' }}>
            Last update by
          </div>
          {lastUpdateTotal > 0 ? (
            <>
              <div
                role="img"
                aria-label={lastUpdateParts
                  .map((part) => `${UPDATED_BY_LABELS[part.key]} ${part.value}`)
                  .join(', ')}
                style={{
                  display: 'flex',
                  height: 16,
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: token.colorFillSecondary,
                }}
              >
                {lastUpdateParts
                  .filter((part) => part.value > 0)
                  .map((part) => (
                    <span
                      key={part.key}
                      style={{
                        width: `${(part.value / lastUpdateTotal) * 100}%`,
                        background: part.color,
                      }}
                    />
                  ))}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 14px',
                  fontSize: 12,
                  marginTop: 8,
                }}
              >
                {lastUpdateParts.map((part) => (
                  <span key={part.key}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: part.color,
                        marginRight: 6,
                      }}
                    />
                    {UPDATED_BY_LABELS[part.key]} {part.value}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <span style={secondary}>No open tickets</span>
          )}
        </Card>
      </div>

      <div style={twoColumn}>
        <Card
          size="small"
          title={`Trend · last ${data.historyDays} days`}
          extra={<span style={{ fontSize: 12 }}>Daily, end of day</span>}
          styles={cardStyles}
        >
          {data.trend.length > 1 ? (
            <ReactApexChart
              type="line"
              height={260}
              options={trendOptions}
              series={trendSeries}
            />
          ) : (
            <Empty />
          )}
        </Card>

        <Card
          size="small"
          title="Aging by building"
          extra={
            <span style={{ fontSize: 12 }}>
              Open · no update 7+ days darker · click to list
            </span>
          }
          styles={cardStyles}
        >
          {data.byBuilding.length === 0 ? (
            <Empty description="No open tickets" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.byBuilding.slice(0, 10).map((group) => (
                <button
                  key={group.key || group.label}
                  type="button"
                  onClick={() =>
                    onDrillDown({
                      view: 'all',
                      buildingId: group.key || undefined,
                    })
                  }
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'block',
                    padding: '3px 0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 12,
                      marginBottom: 3,
                    }}
                  >
                    <span>{group.label || 'Unknown building'}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {group.open}
                      {group.stale7 > 0 ? ` · ${group.stale7} quiet` : ''}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      height: 12,
                      borderRadius: 4,
                      overflow: 'hidden',
                      background: token.colorFillSecondary,
                    }}
                  >
                    <span
                      style={{
                        width: `${(group.stale7 / maxBuildingOpen) * 100}%`,
                        background: token.colorPrimaryActive,
                      }}
                    />
                    <span
                      style={{
                        width: `${((group.open - group.stale7) / maxBuildingOpen) * 100}%`,
                        background: sequentialColor(isDark),
                        opacity: 0.45,
                      }}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div style={twoColumn}>
        <Card
          size="small"
          title="Response & resolution by priority"
          extra={
            <span style={{ fontSize: 12 }}>
              Tickets opened in the last {data.historyDays} days
            </span>
          }
          styles={cardStyles}
        >
          <Table
            size="small"
            pagination={false}
            scroll={{ x: true }}
            dataSource={data.responseByPriority}
            rowKey={(row) => row.priority}
            columns={[
              {
                title: 'Priority',
                key: 'priority',
                render: (_, row: AgingResponsePriority) =>
                  priorityTag(row.priority),
              },
              {
                title: 'Opened',
                dataIndex: 'opened',
                key: 'opened',
                align: 'right' as const,
              },
              {
                title: 'Median first reply',
                key: 'reply',
                align: 'right' as const,
                render: (_, row: AgingResponsePriority) => (
                  <Tooltip
                    title={
                      row.replied > 0
                        ? `${row.replyMetPct}% within SLA target`
                        : undefined
                    }
                  >
                    {formatHours(row.medianFirstReplyHours)}
                  </Tooltip>
                ),
              },
              {
                title: 'Median time to resolve',
                key: 'resolve',
                align: 'right' as const,
                render: (_, row: AgingResponsePriority) =>
                  formatHours(row.medianResolveHours),
              },
              {
                title: 'Still open',
                dataIndex: 'stillOpen',
                key: 'stillOpen',
                align: 'right' as const,
              },
            ]}
          />
        </Card>

        {data.showQueues && (
          <Card
            size="small"
            title={
              <span>
                By support queue{' '}
                <Tag color="purple" style={{ marginLeft: 6 }}>
                  IB users only
                </Tag>
              </span>
            }
            styles={cardStyles}
          >
            <Table
              size="small"
              pagination={false}
              dataSource={data.byQueue}
              rowKey={(group) => group.key || group.label}
              columns={groupColumns('Queue')}
            />
          </Card>
        )}
      </div>

      <div style={{ ...secondary, fontSize: 12, textAlign: 'right' }}>
        "Update" means the last update the customer can see (private notes and
        system entries excluded). Updated{' '}
        {new Date(data.generatedAt).toLocaleString()}
      </div>
    </div>
  );
};

export default AgingSummaryTab;
